import type { ConfigDeployStatusMap } from '@/api/deploy';
import {
    formatLastDeployedVersion,
    getLastDeployedVersion,
} from './config-deploy-status-helpers';

export interface ConfigDeployStatusBadgeProps {
  statusMap: ConfigDeployStatusMap | undefined;
  environmentId: string;
  isError?: boolean;
}

/**
 * Displays the last deployed version for a config file within a given environment.
 * Mirrors the ArtifactDeployStatusBadge pattern from deploy-app-tab.
 *
 * Validates: Requirements 7.2, 7.3
 */
export function ConfigDeployStatusBadge({
  statusMap,
  environmentId,
  isError,
}: ConfigDeployStatusBadgeProps) {
  // Error state — status fetch failed
  if (isError) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
        <span className="inline-block h-2 w-2 rounded-full bg-red-400" />
        Status unavailable
      </span>
    );
  }

  const version = getLastDeployedVersion(statusMap, environmentId);
  const display = formatLastDeployedVersion(version);

  // Never deployed — show placeholder (Req 7.3)
  if (!version) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-content-tertiary">
        <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
        {display}
        <span className="sr-only">never deployed</span>
      </span>
    );
  }

  // Has a last deployed version (Req 7.2)
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-content-secondary">
      <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
      Last Deployed: {display}
    </span>
  );
}
